import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  checkBillingEntitlement,
  fetchBillingEntitlements,
  invalidateBillingEntitlements,
  QuotaExhaustedApiError,
} from '../lib/billing/api'
import { JdPrecheckBlockedError } from '../lib/optimization/optimizationJdPrecheck'
import { supabase } from '../lib/supabase'
import { DEFAULT_MODEL_STACK, type ModelStackId } from '../lib/optimization/modelStacks'
import {
  blockStateForId,
  blockStateForSection,
  createOptimizationRun,
  fetchOptimizationRun,
  startOptimizationRunOnApi,
  subscribeOptimizationRun,
} from '../lib/optimization/optimizationRuns'
import { allBlocksTerminal, hasBlocksInFlight } from '../lib/optimization/blockProgress'
import { trackJdTailoringCompleted, trackJdTailoringStarted } from '../lib/posthogEvents'
import { mergeOptimizationQualityEval } from '../lib/optimization/qualityEval'
import {
  shouldReportStuckOptimizationToSentry,
  stuckOptimizationSentryExtra,
} from '../lib/optimization/optimizationStuckDetect'
import { Sentry } from '../lib/sentry'
import type { QualityGatePanelState } from '../lib/optimization/types'
import type { OptimizationBlockState, ResumeOptimizationRun } from '../lib/optimization/types'

/** Debounce resume reload while blocks complete during an active run. */
export const OPTIMIZATION_BLOCKS_PROGRESS_DEBOUNCE_MS = 2000

/** Keep success/error agent panel visible briefly after the run ends. */
export const OPTIMIZATION_COMPLETION_UI_MS = 6000

/** After run ends, collapse the side panel to the preview dock chip. */
export const OPTIMIZATION_AGENT_AUTO_MINIMIZE_MS = 1200

export function useResumeOptimization(params: {
  resumeId: string | undefined
  workExperience: unknown
  onRunCompleted?: () => void
  onBlocksProgress?: () => void
}) {
  const { resumeId, workExperience, onRunCompleted, onBlocksProgress } = params
  const [jdModalOpen, setJdModalOpen] = useState(false)
  const [modelStack, setModelStack] = useState<ModelStackId>(DEFAULT_MODEL_STACK)
  const [activeRun, setActiveRun] = useState<ResumeOptimizationRun | null>(null)
  const [starting, setStarting] = useState(false)
  const [startError, setStartError] = useState<string | null>(null)
  const [failureDialogBlock, setFailureDialogBlock] = useState<OptimizationBlockState | null>(
    null
  )
  const [quotaModalOpen, setQuotaModalOpen] = useState(false)
  const [quotaModalTier, setQuotaModalTier] = useState<'free' | 'pro'>('free')
  const [quotaModalUsed, setQuotaModalUsed] = useState(0)
  const [quotaModalCap, setQuotaModalCap] = useState(0)
  const [quotaModalReason, setQuotaModalReason] = useState<string | null>(null)
  const [quotaModalDuplicateLayer, setQuotaModalDuplicateLayer] = useState<string | null>(null)
  const [qualityGatePanel, setQualityGatePanel] = useState<QualityGatePanelState>('hidden')
  const [agentPanelMinimized, setAgentPanelMinimized] = useState(false)
  const [agentPanelDismissed, setAgentPanelDismissed] = useState(false)
  /** User collapsed agent panel to rail — do not force-expand on run completion. */
  const agentPanelUserMinimizedRef = useRef(false)
  const qualityGateRunIdRef = useRef<string | null>(null)
  const blocksProgressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const completionUiTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const agentAutoMinimizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const terminalRunHandledRef = useRef<string | null>(null)
  const stuckSentryReportedRef = useRef<string | null>(null)
  const onRunCompletedRef = useRef(onRunCompleted)
  const onBlocksProgressRef = useRef(onBlocksProgress)
  onRunCompletedRef.current = onRunCompleted
  onBlocksProgressRef.current = onBlocksProgress
  const [runUiStartedAtMs, setRunUiStartedAtMs] = useState<number | null>(null)
  const [agentUiTick, setAgentUiTick] = useState(0)
  const [completionUiRun, setCompletionUiRun] = useState<ResumeOptimizationRun | null>(null)

  const isOptimizing = activeRun?.status === 'running' || activeRun?.status === 'pending'
  const activeBlocks = activeRun?.blocks ?? []
  const isRunFinalizing =
    isOptimizing && activeBlocks.length > 0 && allBlocksTerminal(activeBlocks)
  const isBlocksInFlight =
    isOptimizing && activeBlocks.length > 0 && hasBlocksInFlight(activeBlocks)
  const showOptimizationAgentUi = starting || isOptimizing || completionUiRun != null
  const runForAgentUi = completionUiRun ?? activeRun

  const qualityEval = useMemo(() => {
    if (!activeRun?.blocks?.length) return null
    if (activeRun.status !== 'completed' && activeRun.status !== 'failed') return null
    return mergeOptimizationQualityEval(
      activeRun.blocks,
      activeRun.model_stack,
      activeRun.quality_eval ?? null
    )
  }, [activeRun])

  const openJdModal = useCallback(() => {
    setStartError(null)
    setJdModalOpen(true)
  }, [])

  const closeJdModal = useCallback(() => {
    setJdModalOpen(false)
  }, [])

  const closeQuotaModal = useCallback(() => setQuotaModalOpen(false), [])

  const showQuotaModal = useCallback(
    (payload: {
      tier?: string
      used?: number
      cap?: number
      remaining?: number
      reason?: string | null
      duplicate_layer?: string | null
    }) => {
      setQuotaModalTier(payload.tier === 'pro' ? 'pro' : 'free')
      const cap = payload.cap ?? 0
      const used =
        payload.used ??
        (cap > 0 && payload.remaining != null ? Math.max(0, cap - payload.remaining) : cap)
      setQuotaModalUsed(used)
      setQuotaModalCap(cap)
      setQuotaModalReason(payload.reason ?? null)
      setQuotaModalDuplicateLayer(payload.duplicate_layer ?? null)
      setQuotaModalOpen(true)
      setJdModalOpen(false)
    },
    []
  )

  const startOptimization = useCallback(
    async (jobDescription: string, stack: ModelStackId = modelStack) => {
      if (!resumeId) return
      const jd = jobDescription.trim()
      if (jd.length < 80) {
        setStartError('Paste the full job description (at least 80 characters).')
        return
      }

      setStarting(true)
      setStartError(null)
      setRunUiStartedAtMs(Date.now())
      setJdModalOpen(false)
      try {
        const entitlements = await fetchBillingEntitlements()
        if (entitlements?.billing_enabled) {
          const check = await checkBillingEntitlement()
          if (check && !check.allowed) {
            showQuotaModal({
              tier: check.tier,
              used: check.used,
              cap: check.cap,
              remaining: check.remaining,
            })
            return
          }
        }

        const run = await createOptimizationRun({
          resumeId,
          jobDescription: jd,
          workExperience,
          modelStack: stack,
        })
        terminalRunHandledRef.current = null
        stuckSentryReportedRef.current = null
        if (completionUiTimerRef.current) {
          clearTimeout(completionUiTimerRef.current)
          completionUiTimerRef.current = null
        }
        if (agentAutoMinimizeTimerRef.current) {
          clearTimeout(agentAutoMinimizeTimerRef.current)
          agentAutoMinimizeTimerRef.current = null
        }
        setCompletionUiRun(null)
        setActiveRun(run)
        qualityGateRunIdRef.current = null
        setQualityGatePanel('hidden')
        agentPanelUserMinimizedRef.current = false
        setAgentPanelMinimized(false)
        setAgentPanelDismissed(false)
        trackJdTailoringStarted(resumeId)
        void startOptimizationRunOnApi(run.id)
          .then(() => {
            invalidateBillingEntitlements()
          })
          .catch((e) => {
            if (e instanceof QuotaExhaustedApiError) {
              showQuotaModal({
                tier: e.tier,
                used: e.cap > 0 ? e.cap - e.remaining : 0,
                cap: e.cap,
                remaining: e.remaining,
                reason: e.reason,
                duplicate_layer: e.duplicateLayer,
              })
              return
            }
            if (e instanceof JdPrecheckBlockedError) {
              setStartError(e.message)
              setJdModalOpen(true)
              setActiveRun(null)
              return
            }
            setStartError(e instanceof Error ? e.message : String(e))
            setJdModalOpen(true)
          })
        setActiveRun((prev) => (prev ? { ...prev, status: 'running' } : prev))
      } catch (e) {
        setStartError(e instanceof Error ? e.message : String(e))
        setJdModalOpen(true)
      } finally {
        setStarting(false)
      }
    },
    [modelStack, resumeId, showQuotaModal, workExperience]
  )

  useEffect(() => {
    if (!showOptimizationAgentUi) return undefined
    const interval = setInterval(() => {
      setAgentUiTick((t) => t + 1)
    }, 1500)
    return () => clearInterval(interval)
  }, [showOptimizationAgentUi])

  useEffect(() => {
    if (!activeRun?.id || !showOptimizationAgentUi) return undefined
    if (activeRun.status !== 'running' && activeRun.status !== 'pending') return undefined
    if (
      !shouldReportStuckOptimizationToSentry(
        activeRun,
        runUiStartedAtMs,
        stuckSentryReportedRef.current
      )
    ) {
      return undefined
    }

    stuckSentryReportedRef.current = activeRun.id
    Sentry.captureMessage('Optimization UI stuck: no block progress', {
      level: 'warning',
      tags: {
        optimization_stuck_ux: 'true',
        optimization_run_id: activeRun.id,
      },
      extra: stuckOptimizationSentryExtra(activeRun, runUiStartedAtMs),
    })
  }, [
    activeRun,
    activeRun?.blocks,
    activeRun?.id,
    activeRun?.status,
    agentUiTick,
    runUiStartedAtMs,
    showOptimizationAgentUi,
  ])

  useEffect(() => {
    if (!activeRun?.id) return undefined
    if (activeRun.status !== 'running' && activeRun.status !== 'pending') return undefined

    const unsubscribe = subscribeOptimizationRun(
      activeRun.id,
      (run) => {
        setActiveRun(run)
        if (run.status === 'completed' || run.status === 'failed') {
          if (terminalRunHandledRef.current !== run.id) {
            terminalRunHandledRef.current = run.id
            invalidateBillingEntitlements()
            onRunCompletedRef.current?.()
            if (resumeId) {
              trackJdTailoringCompleted(
                resumeId,
                run.status === 'completed' ? 'completed' : 'failed',
              )
            }
            if (qualityGateRunIdRef.current !== run.id) {
              qualityGateRunIdRef.current = run.id
              // Inline feedback in agent panel first; dock chip after panel hides.
              setQualityGatePanel('hidden')
            }
            if (!agentPanelUserMinimizedRef.current) {
              setAgentPanelMinimized(false)
            }
            setAgentPanelDismissed(false)
            setCompletionUiRun(run)
            if (agentAutoMinimizeTimerRef.current) {
              clearTimeout(agentAutoMinimizeTimerRef.current)
            }
            if (!agentPanelUserMinimizedRef.current) {
              agentAutoMinimizeTimerRef.current = setTimeout(() => {
                agentAutoMinimizeTimerRef.current = null
                setAgentPanelMinimized(true)
              }, OPTIMIZATION_AGENT_AUTO_MINIMIZE_MS)
            }
            if (completionUiTimerRef.current) {
              clearTimeout(completionUiTimerRef.current)
            }
            completionUiTimerRef.current = setTimeout(() => {
              completionUiTimerRef.current = null
              setCompletionUiRun(null)
              setQualityGatePanel((prev) => (prev === 'hidden' ? 'minimized' : prev))
            }, OPTIMIZATION_COMPLETION_UI_MS)
          }
        }
      },
      () => {
        if (blocksProgressTimerRef.current) {
          clearTimeout(blocksProgressTimerRef.current)
        }
        blocksProgressTimerRef.current = setTimeout(() => {
          blocksProgressTimerRef.current = null
          onBlocksProgressRef.current?.()
        }, OPTIMIZATION_BLOCKS_PROGRESS_DEBOUNCE_MS)
      }
    )

    return () => {
      if (blocksProgressTimerRef.current) {
        clearTimeout(blocksProgressTimerRef.current)
        blocksProgressTimerRef.current = null
      }
      unsubscribe()
    }
  }, [activeRun?.id, activeRun?.status, resumeId])

  useEffect(() => {
    return () => {
      if (completionUiTimerRef.current) {
        clearTimeout(completionUiTimerRef.current)
        completionUiTimerRef.current = null
      }
      if (agentAutoMinimizeTimerRef.current) {
        clearTimeout(agentAutoMinimizeTimerRef.current)
        agentAutoMinimizeTimerRef.current = null
      }
    }
  }, [])

  const getBlockForSection = useCallback(
    (sectionType: string, companyName?: string) => {
      if (!activeRun?.blocks?.length) return undefined
      return blockStateForSection(activeRun.blocks, sectionType, companyName)
    },
    [activeRun?.blocks]
  )

  const getBlockById = useCallback(
    (blockId: string) => {
      if (!activeRun?.blocks?.length) return undefined
      return blockStateForId(activeRun.blocks, blockId)
    },
    [activeRun?.blocks]
  )

  const blocksById = useMemo(() => {
    const map = new Map<string, OptimizationBlockState>()
    for (const b of activeRun?.blocks || []) {
      map.set(b.block_id, b)
    }
    return map
  }, [activeRun?.blocks])

  const resumeActiveRunOnMount = useCallback(async () => {
    if (!resumeId) return
    const { data } = await supabase
      .from('resume_optimization_runs')
      .select('*')
      .eq('resume_id', resumeId)
      .in('status', ['pending', 'running'])
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()
    if (data) {
      const run = await fetchOptimizationRun(String(data.id))
      if (run) {
        setActiveRun(run)
        setRunUiStartedAtMs(Date.parse(run.created_at) || Date.now())
      }
      return
    }

    const { data: latestTerminal } = await supabase
      .from('resume_optimization_runs')
      .select('id')
      .eq('resume_id', resumeId)
      .in('status', ['completed', 'failed'])
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()
    if (!latestTerminal?.id) return

    const terminalRun = await fetchOptimizationRun(String(latestTerminal.id))
    if (!terminalRun?.quality_eval || terminalRun.quality_eval.primary_pass) return

    setActiveRun(terminalRun)
    if (qualityGateRunIdRef.current !== terminalRun.id) {
      qualityGateRunIdRef.current = terminalRun.id
      setQualityGatePanel('minimized')
    }
  }, [resumeId])

  useEffect(() => {
    void resumeActiveRunOnMount()
  }, [resumeActiveRunOnMount])

  const minimizeQualityGate = useCallback(() => setQualityGatePanel('minimized'), [])
  const expandQualityGate = useCallback(() => {
    setAgentPanelMinimized(true)
    setQualityGatePanel('open')
  }, [])
  const dismissQualityGate = useCallback(() => setQualityGatePanel('hidden'), [])
  const minimizeAgentPanel = useCallback(() => {
    agentPanelUserMinimizedRef.current = true
    setAgentPanelMinimized(true)
    const terminal =
      activeRun?.status === 'completed' || activeRun?.status === 'failed'
    // After tailoring ends, keep a rail reopen target (dock chip) instead of hiding both panels.
    setQualityGatePanel(terminal ? 'minimized' : 'hidden')
  }, [activeRun?.status])
  const expandAgentPanel = useCallback(() => {
    agentPanelUserMinimizedRef.current = false
    setAgentPanelMinimized(false)
    setQualityGatePanel('hidden')
  }, [])
  const dismissAgentPanel = useCallback(() => {
    minimizeAgentPanel()
  }, [minimizeAgentPanel])

  return {
    jdModalOpen,
    openJdModal,
    closeJdModal,
    modelStack,
    setModelStack,
    startOptimization,
    starting,
    startError,
    activeRun,
    runForAgentUi,
    isOptimizing,
    isRunFinalizing,
    isBlocksInFlight,
    getBlockForSection,
    getBlockById,
    blocksById,
    failureDialogBlock,
    setFailureDialogBlock,
    qualityGatePanel,
    qualityEval,
    minimizeQualityGate,
    expandQualityGate,
    dismissQualityGate,
    agentPanelMinimized,
    agentPanelDismissed,
    minimizeAgentPanel,
    expandAgentPanel,
    dismissAgentPanel,
    quotaModalOpen,
    quotaModalTier,
    quotaModalUsed,
    quotaModalCap,
    quotaModalReason,
    quotaModalDuplicateLayer,
    closeQuotaModal,
    runUiStartedAtMs,
    showOptimizationAgentUi,
    agentUiTick,
    completionUiRun,
  }
}
